import { queryOptions } from '@tanstack/react-query';
import type { OptionCallPut } from 'src/features/symbol-picker/types';
import { getUnderlyingSymbolFromInstrumentString } from 'src/lib/instrumentUtils';
import type { SymbolResult, SymbolSearchService } from 'src/services/SymbolSearchService';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'queries:symbolSearchService' });

const TEN_SECONDS = 10 * 1000;

export const getSymbolSearchServiceQuery = (searchText: string, symbolSearchService: SymbolSearchService) =>
    queryOptions({
        queryKey: [...symbolSearchService.queryKey, searchText],
        queryFn: async () => await symbolSearchService.search(searchText),
        staleTime: TEN_SECONDS,
        gcTime: TEN_SECONDS,
        // The searchable symbol universe is global and does not depend on the user's
        // subscription tier, so skip the entitlements reset (the result set can
        // exceed 60k rows and resetting it mid-subscription-change triggers a
        // refetch + select storm).
        meta: { entitlementInsensitive: true },
    });

/**
 * Filter results for the first occurrence of an instrument (options)
 *
 * @param data
 * @returns SymbolResult[]
 */
export const selectFirstMatchPerUnderlyingSymbol = (data: SymbolResult[]): SymbolResult[] => {
    const firstMatches = new Map<string, SymbolResult>();
    for (const result of data) {
        const underlyingSymbol = getUnderlyingSymbolFromInstrumentString(result.symbol);

        // Only keep the first occurrence of each underlying symbol
        if (!firstMatches.has(underlyingSymbol)) {
            firstMatches.set(underlyingSymbol, result);
        }
    }

    return Array.from(firstMatches.values());
};

/**
 * Filter results to unique underlying symbols
 *
 * the symbol value will also be the underlying symbol vs full option symbol
 *
 * @param data
 * @returns SymbolResult[]
 */
export function selectUniqueSymbols(data: SymbolResult[]): SymbolResult[] {
    const symbolResults = new Map<string, SymbolResult>();
    for (const x of data) {
        const symbol = getUnderlyingSymbolFromInstrumentString(x.symbol);
        if (!symbolResults.has(symbol)) {
            symbolResults.set(symbol, { symbol, info: x.info });
        }
    }

    return Array.from(symbolResults.values());
}

type SelectExactMatchesParams<T = SymbolResult[]> = {
    underlyingSymbol?: string;
    expirationDate?: string;
    strikePrice?: number;
    optionCallPut?: OptionCallPut;
    select?: (data: SymbolResult[]) => T;
};

/**
 * Filter results to match only the exact symbol being searched for
 *
 * @param data
 * @returns SymbolResult[] or the return type of the select function if provided
 */
export const selectExactMatches =
    <T = SymbolResult[]>(params: SelectExactMatchesParams<T>) =>
    (data: SymbolResult[]): T => {
        const values = data.filter((x) => {
            if (params.underlyingSymbol) {
                if (
                    getUnderlyingSymbolFromInstrumentString(x.symbol).toLowerCase() !==
                    params.underlyingSymbol.toLowerCase()
                ) {
                    return false;
                }
            }

            if (params.expirationDate) {
                if (x.symbol.slice(6, 12) !== params.expirationDate) {
                    return false;
                }
            }

            if (params.optionCallPut) {
                if (x.symbol.slice(12, 13) !== params.optionCallPut) {
                    return false;
                }
            }

            if (params.strikePrice !== undefined) {
                if (Number(x.symbol.slice(13, 21)) / 1000 !== params.strikePrice) {
                    return false;
                }
            }

            return true;
        });

        if (params.select) {
            const refinedValues = params.select(values);
            if (TRACE) {
                log.trace({
                    message: 'selectExactMatches',
                    params,
                    valuesCount: values.length,
                    dataCount: data.length,
                    refinedValuesCount: Array.isArray(refinedValues) ? refinedValues.length : undefined,
                });
            }
            return refinedValues as T;
        }

        if (TRACE) {
            log.trace({
                message: 'selectExactMatches',
                params,
                valuesCount: values.length,
                dataCount: data.length,
            });
        }

        return values as T;
    };

/**
 * Filter results to unique expiration dates
 *
 * @param data
 * @returns string[]
 */
export function selectDates(data: SymbolResult[]) {
    const dates = new Set<string>();
    for (const x of data) {
        dates.add(x.symbol.slice(6, 12));
    }

    return Array.from(dates);
}

/**
 * Filter results to unique prices
 *
 * @param data
 * @returns number[]
 */
export function selectPrices(data: SymbolResult[]) {
    const prices = new Set<number>();
    for (const x of data) {
        if (x.symbol.length === 21) {
            const raw = Number(x.symbol.slice(13, 21)) / 1000;
            if (!Number.isNaN(raw)) {
                prices.add(raw);
            }
        }
    }

    return Array.from(prices);
}

/**
 * Filter results to unique option rights
 *
 * @param data
 * @returns OptionCallPut[]
 */
export function selectRights(data: SymbolResult[]) {
    const rights = new Set<OptionCallPut>();
    for (const x of data) {
        rights.add(x.symbol.slice(12, 13) as OptionCallPut);
        // there can only be two rights, C and P if we have both let's short circuit this loop and return them
        if (rights.size === 2) {
            return Array.from(rights);
        }
    }

    return Array.from(rights);
}
